import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NavVisibilityProvider } from './contexts/NavVisibilityContext';
import { Toaster } from 'react-hot-toast';
import { AnimatePresence } from 'framer-motion';
import Layout from './components/Layout';
import Login from './pages/Login';

// Cada tela vira um arquivo próprio, baixado só quando é aberta. Antes o
// primeiro acesso trazia o sistema inteiro de uma vez.
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Activations = lazy(() => import('./pages/Activations'));
const Resellers = lazy(() => import('./pages/Resellers'));
const Transactions = lazy(() => import('./pages/Transactions'));
const Admin = lazy(() => import('./pages/Admin'));
const Coupons = lazy(() => import('./pages/Coupons'));
const Announcements = lazy(() => import('./pages/Announcements'));
const Profile = lazy(() => import('./pages/Profile'));
const MyStore = lazy(() => import('./pages/MyStore'));
const PaymentMethods = lazy(() => import('./pages/PaymentMethods'));
const StoreStats = lazy(() => import('./pages/StoreStats'));
const BuyCredits = lazy(() => import('./pages/BuyCredits'));
const InviteRegister = lazy(() => import('./pages/InviteRegister'));
const SMTPConfig = lazy(() => import('./pages/SMTPConfig'));
const Premium = lazy(() => import('./pages/Premium'));
const PremiumAdmin = lazy(() => import('./pages/PremiumAdmin'));
const PremiumStorePublic = lazy(() => import('./pages/PremiumStorePublic'));
const PremiumStoreAnnouncements = lazy(() => import('./pages/PremiumStoreAnnouncements'));
const Affiliate = lazy(() => import('./pages/Affiliate'));
const StoreNotFound = lazy(() => import('./pages/StoreNotFound'));

import Maintenance from './pages/Maintenance';
import { Zap } from 'lucide-react';
import './App.css';
import { useState, useEffect } from 'react';

const getBackendUrl = () => {
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};

const MaintenanceWrapper = ({ children }) => {
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [checking, setChecking] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    checkMaintenance();
  }, []);

  const checkMaintenance = async () => {
    try {
      const response = await fetch(`${getBackendUrl()}/api/public/maintenance-check`);
      if (!response.ok) {
        throw new Error('Failed to fetch maintenance status');
      }
      const data = await response.json();
      setMaintenanceMode(data.maintenance);
    } catch (error) {
      console.error('Error checking maintenance:', error);
      // Em caso de erro, assume que não está em manutenção
      setMaintenanceMode(false);
    } finally {
      setChecking(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  // If in maintenance and not admin, show maintenance page
  if (maintenanceMode && (!user || (user.role !== 'admin' && !user.is_admin))) {
    return <Maintenance />;
  }

  return children;
};

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" />;
};

const App = () => {
  return (
    <AuthProvider>
      <NavVisibilityProvider>
        <Toaster
          position="bottom-right"
          gutter={10}
          containerClassName="toast-container"
          containerStyle={{ bottom: 20, right: 12 }}
        />
        <BrowserRouter>
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center bg-[#0B0912]">
            <div className="w-9 h-9 rounded-full border-2 border-[#272034] border-t-[#9B6BFF] animate-spin" />
          </div>
        }>
        <MaintenanceWrapper>
          <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/convite/:slug" element={<InviteRegister />} />
          <Route path="/index/:slug" element={<PremiumStorePublic />} />
          <Route path="/affiliate/:slug" element={<Affiliate />} />
          <Route path="/store-not-found" element={<StoreNotFound />} />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/activations"
            element={
              <PrivateRoute>
                <Layout>
                  <Activations />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/resellers"
            element={
              <PrivateRoute>
                <Layout>
                  <Resellers />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/transactions"
            element={
              <PrivateRoute>
                <Layout>
                  <Transactions />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <PrivateRoute>
                <Layout>
                  <Admin />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/coupons"
            element={
              <PrivateRoute>
                <Layout>
                  <Coupons />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/announcements"
            element={
              <PrivateRoute>
                <Layout>
                  <Announcements />
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/smtp-config"
            element={
              <PrivateRoute>
                <Layout>
                  <SMTPConfig />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <Layout>
                  <Profile />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/my-store"
            element={
              <PrivateRoute>
                <Layout>
                  <MyStore />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/my-store/stats"
            element={
              <PrivateRoute>
                <Layout>
                  <StoreStats />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/payment-methods"
            element={
              <PrivateRoute>
                <Layout>
                  <PaymentMethods />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/premium/announcements"
            element={
              <PrivateRoute>
                <Layout>
                  <PremiumStoreAnnouncements />
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/buy-credits"
            element={
              <PrivateRoute>
                <Layout>
                  <BuyCredits />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/premium"
            element={
              <PrivateRoute>
                <Layout>
                  <Premium />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/premium-admin"
            element={
              <PrivateRoute>
                <Layout>
                  <PremiumAdmin />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route path="/" element={<Navigate to="/dashboard" />} />
          </Routes>
        </MaintenanceWrapper>
        </Suspense>
      </BrowserRouter>
      </NavVisibilityProvider>
    </AuthProvider>
  );
};

export default App;
